type Coordinate = readonly [number, number];

const EPSILON = 1e-10;

const cross = (a: Coordinate, b: Coordinate) => (a[0] * b[1]) - (a[1] * b[0]);

const subtract = (a: Coordinate, b: Coordinate): Coordinate => [a[0] - b[0], a[1] - b[1]];

const isFiniteCoordinate = (coordinate: Coordinate) => (
    Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1])
);

const isPointOnSegment = (point: Coordinate, start: Coordinate, end: Coordinate) => {
    const segment = subtract(end, start);
    const offset = subtract(point, start);
    const scale = Math.max(1, Math.abs(segment[0]), Math.abs(segment[1]));
    if (Math.abs(cross(segment, offset)) > EPSILON * scale) {
        return false;
    }

    return point[0] >= Math.min(start[0], end[0]) - EPSILON
        && point[0] <= Math.max(start[0], end[0]) + EPSILON
        && point[1] >= Math.min(start[1], end[1]) - EPSILON
        && point[1] <= Math.max(start[1], end[1]) + EPSILON;
};

const isPointOnRing = (point: Coordinate, ring: Coordinate[]) => ring.some((start, index) => (
    isPointOnSegment(point, start, ring[(index + 1) % ring.length])
));

const isPointStrictlyInsideRing = (point: Coordinate, ring: Coordinate[]) => {
    let inside = false;
    for (let index = 0, previous = ring.length - 1; index < ring.length; previous = index, index += 1) {
        const currentPoint = ring[index];
        const previousPoint = ring[previous];
        const crossesRay = (currentPoint[1] > point[1]) !== (previousPoint[1] > point[1]);
        if (
            crossesRay
            && point[0] < ((previousPoint[0] - currentPoint[0]) * (point[1] - currentPoint[1]))
                / (previousPoint[1] - currentPoint[1]) + currentPoint[0]
        ) {
            inside = !inside;
        }
    }
    return inside;
};

const normalizeRing = (value: unknown): Coordinate[] => (
    Array.isArray(value)
        ? value
            .filter((coordinate): coordinate is number[] => Array.isArray(coordinate) && coordinate.length >= 2)
            .map((coordinate) => [Number(coordinate[0]), Number(coordinate[1])] as Coordinate)
            .filter(isFiniteCoordinate)
        : []
);

const normalizePolygons = (geometry: any): Coordinate[][][] => {
    const candidate = geometry?.type === 'Feature' ? geometry.geometry : geometry;
    if (candidate?.type === 'Polygon' && Array.isArray(candidate.coordinates)) {
        return [candidate.coordinates.map(normalizeRing).filter((ring: Coordinate[]) => ring.length >= 3)];
    }
    if (candidate?.type === 'MultiPolygon' && Array.isArray(candidate.coordinates)) {
        return candidate.coordinates.map((polygon: unknown[]) => (
            polygon.map(normalizeRing).filter((ring: Coordinate[]) => ring.length >= 3)
        ));
    }
    return [];
};

const isPointInsidePolygon = (point: Coordinate, polygon: Coordinate[][]) => {
    const outerRing = polygon[0];
    if (!outerRing) return false;
    if (isPointOnRing(point, outerRing)) return true;
    if (!isPointStrictlyInsideRing(point, outerRing)) return false;

    for (const hole of polygon.slice(1)) {
        if (isPointOnRing(point, hole)) return true;
        if (isPointStrictlyInsideRing(point, hole)) return false;
    }
    return true;
};

const isPointInsideGeometry = (point: Coordinate, polygons: Coordinate[][][]) => (
    polygons.some((polygon) => isPointInsidePolygon(point, polygon))
);

const addIntersectionParameters = (
    parameters: number[],
    pathStart: Coordinate,
    pathEnd: Coordinate,
    edgeStart: Coordinate,
    edgeEnd: Coordinate
) => {
    const pathVector = subtract(pathEnd, pathStart);
    const edgeVector = subtract(edgeEnd, edgeStart);
    const offset = subtract(edgeStart, pathStart);
    const denominator = cross(pathVector, edgeVector);

    if (Math.abs(denominator) > EPSILON) {
        const pathParameter = cross(offset, edgeVector) / denominator;
        const edgeParameter = cross(offset, pathVector) / denominator;
        if (
            pathParameter >= -EPSILON && pathParameter <= 1 + EPSILON
            && edgeParameter >= -EPSILON && edgeParameter <= 1 + EPSILON
        ) {
            parameters.push(Math.max(0, Math.min(1, pathParameter)));
        }
        return;
    }

    if (Math.abs(cross(offset, pathVector)) > EPSILON) return;
    const squaredLength = (pathVector[0] ** 2) + (pathVector[1] ** 2);
    if (squaredLength <= EPSILON) return;

    [edgeStart, edgeEnd].forEach((edgePoint) => {
        const edgeOffset = subtract(edgePoint, pathStart);
        const parameter = ((edgeOffset[0] * pathVector[0]) + (edgeOffset[1] * pathVector[1])) / squaredLength;
        if (parameter >= -EPSILON && parameter <= 1 + EPSILON) {
            parameters.push(Math.max(0, Math.min(1, parameter)));
        }
    });
};

const isSegmentInsidePolygons = (
    start: Coordinate,
    end: Coordinate,
    polygons: Coordinate[][][]
) => {
    if (!isFiniteCoordinate(start) || !isFiniteCoordinate(end)) return false;
    if (!isPointInsideGeometry(start, polygons) || !isPointInsideGeometry(end, polygons)) return false;

    const parameters = [0, 1];
    polygons.forEach((polygon) => {
        polygon.forEach((ring) => {
            ring.forEach((edgeStart, index) => {
                addIntersectionParameters(
                    parameters,
                    start,
                    end,
                    edgeStart,
                    ring[(index + 1) % ring.length]
                );
            });
        });
    });

    const sortedParameters = Array.from(new Set(parameters.map((value) => value.toFixed(12))))
        .map(Number)
        .sort((a, b) => a - b);
    for (let index = 0; index < sortedParameters.length - 1; index += 1) {
        const midpointParameter = (sortedParameters[index] + sortedParameters[index + 1]) / 2;
        const midpoint: Coordinate = [
            start[0] + ((end[0] - start[0]) * midpointParameter),
            start[1] + ((end[1] - start[1]) * midpointParameter),
        ];
        if (!isPointInsideGeometry(midpoint, polygons)) return false;
    }
    return true;
};

export const getInvalidPathSegmentIndexes = (
    coordinates: Coordinate[],
    geometry: any
): number[] => {
    const polygons = normalizePolygons(geometry);
    if (!polygons.length || coordinates.length < 2) return [];

    return coordinates.slice(0, -1).flatMap((coordinate, index) => (
        isSegmentInsidePolygons(coordinate, coordinates[index + 1], polygons) ? [] : [index]
    ));
};

export const isPathInsideArea = (coordinates: Coordinate[], geometry: any) => (
    getInvalidPathSegmentIndexes(coordinates, geometry).length === 0
);
